import Helpers from './helpers';

const boolRegex = /^(?:(1|t(?:rue)?)|(0|f(?:alse)?))$/i;
const intRegex = /^\d+$/;

const handleClose = (setState) => {
  setState.setName('');
  setState.setDescription('');
  setState.setDeviceId(null);
  setState.setEditOpen(false);
};

const handleConsumerSelect = (selection, state, setState) => {
  let consumers = JSON.parse(JSON.stringify(state.consumerId || []));

  if (consumers.includes(selection.value)) {
    consumers = consumers.filter(id => id !== selection.value);
  }
  else {
    consumers.push(selection.value);
  }

  setState.setConsumerId(consumers);
};

// A description describes this owner's stream from this provider. Change either and it is a
// different stream, so whatever was written for the old one is released and the dialog
// defaults it again from the new name. A device change does not reset it: the stream is still
// the same owner's feed from the same provider, just arriving from another machine.
const handleOwnerSelect = (selection, setState, setFieldValue) => {
  setState.setOwnerId(selection.value);
  // A device belongs to exactly one owner, so a device picked under the previous owner is
  // not among the choices any more - and would otherwise be saved against the new one.
  setState.setDeviceId(null);

  setState.setDescription('');
  setFieldValue('description', '');
};

const handleDeviceSelect = (selection, setState) => {
  setState.setDeviceId(selection.value);
};

const handleCronUpdate = (value, setState) => {
  setState.setCron(value);
};

const handleProviderSelect = async (selection, values, resetForm, setState, getProperties, nextProvider) => {
  const providerScheme = nextProvider?.partition_scheme || null;

  setState.setProviderId(selection.value);
  // Only a PC Agent feed is tied to a device. Moving to any other provider has to let the
  // device go, or a stale id would be written to a stream that has no device behind it.
  if (!Helpers.isDeviceProvider(nextProvider)) setState.setDeviceId(null);

  setState.setName(values.name);
  // Emptied here as well as in resetForm below, so the state the dialog seeds a fresh form
  // from agrees with the form it just built.
  setState.setDescription('');
  setState.setCron(values.cron);
  setState.setLastRetrievalDate(values.last_retrieval_date);

  const newFields = !getProperties.data ? null : getProperties.data;

  // Rules the user actually wrote survive the provider change. Anything else - nothing yet,
  // or an untouched example for the provider they moved away from - is reseeded for the new
  // scheme. Leaving partition_configs out of resetForm dropped the field entirely, so the
  // editor came up with no rows and nothing to type a file key into.
  const rules = values.partition_configs;
  const keepRules = Boolean(rules) && !Helpers.isExampleValue(rules);

  resetForm({
    values: {
      name: values.name,
      // Released, not carried over: see handleOwnerSelect.
      description: '',
      provider_id: selection.value,
      partition_configs: keepRules ? rules : Helpers.exampleForScheme(providerScheme),
      ...Helpers.prettifyProperties(newFields)
    }
  });
};

const validate = async (values, getProperties, expectedName = '') => {
  const fields = !getProperties.data ? null : getProperties.data;

  const validationObject = Helpers.generateValidationSchema(fields);

  let errors = {};

  for (let field in validationObject) {
    try {
      await validationObject[field].validate(values[field]);

      switch (validationObject[field].type) {
        case "boolean":
          if (!boolRegex.test(values[field])) {
            errors[field] = `${values[field]} is not a valid boolean value for field ${field}`;
          }
          break;
        case "integer":
          if (!intRegex.test(values[field])) {
            errors[field] = `${values[field]} is not a valid numeric value for field ${field}`;
          }
          break;
      }

    } catch (err) {
      errors[field] = err.errors[0];
    }
    // TODO will probably have to turn an array of errors from Yup into a single string with commas or something (Formik doesn't tell you how to do multiple errors)
  }

  // Validated separately from the loop above, which is driven by the provider's own
  // property list. partition_configs is not one of those - it is a column on every
  // data stream - and a malformed value here silently stops rows being split out to
  // the child owners, so it is worth catching before save rather than in dbt.
  const partitionConfigsError = Helpers.validatePartitionConfigs(values.partition_configs);
  if (partitionConfigsError) errors.partition_configs = partitionConfigsError;

  // The name is worked out from the selections and shown rather than typed, and the dialog
  // keeps it in step as those selections change. This only fires if that sync has not landed
  // yet - it holds Save until it has, rather than let a stale name reach a column with a
  // UNIQUE key on it.
  if (expectedName && values.name !== expectedName) {
    errors.name = `Must be "${expectedName}".`;
  }

  return errors;
};

export default {handleClose, handleConsumerSelect, handleDeviceSelect, handleOwnerSelect, handleCronUpdate,
  handleProviderSelect, validate};